import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import {
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';

export interface SearchableColumn {
  /** Field key matching the DataGrid column `field` value. Use `'all'` for global quick-filter. */
  value: string;
  /** Display label shown in the dropdown. */
  label: string;
  /** `'number'` restricts input to digits and uses the `=` filter operator. */
  type: 'string' | 'number';
}

interface ColumnScopedSearchBarProps {
  /** Columns to expose in the dropdown. Must include an `{ value: 'all', label: '...' }` entry for global search. */
  columns: SearchableColumn[];
}

const isNumeric = (columns: SearchableColumn[], column: string) =>
  columns.find((c) => c.value === column)?.type === 'number';

function getSearchStateFromFilterModel(filterModel: any): {
  text: string;
  column: string;
  hasActiveSearch: boolean;
} {
  const sentinel = filterModel?.items?.find(
    (item: any) => item?.id === '__column_search__',
  );

  if (sentinel?.columnField && sentinel?.value != null) {
    return {
      text: String(sentinel.value),
      column: String(sentinel.columnField),
      hasActiveSearch: true,
    };
  }

  const quick = filterModel?.quickFilterValues?.[0];
  if (quick != null && String(quick).length > 0) {
    return {
      text: String(quick),
      column: 'all',
      hasActiveSearch: true,
    };
  }

  return { text: '', column: 'all', hasActiveSearch: false };
}

export function ColumnScopedSearchBar({ columns }: ColumnScopedSearchBarProps) {
  const apiRef = useGridApiContext();
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const [selectedColumn, setSelectedColumn] = useState('all');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceVersionRef = useRef(0);

  // Reactive filter model — re-renders when any external change happens
  // (filter panel clear, external setFilterModel calls, etc.)
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  useEffect(() => {
    const external = getSearchStateFromFilterModel(filterModel);

    // External model changes must cancel pending local debounce to avoid stale overwrite.
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    debounceVersionRef.current += 1;

    if (external.hasActiveSearch) {
      setSearchText((prev) => (prev === external.text ? prev : external.text));
      setSelectedColumn((prev) =>
        prev === external.column ? prev : external.column,
      );
      return;
    }

    // No active external search: clear only text, keep user's selected column.
    setSearchText((prev) => (prev === '' ? prev : ''));
  }, [filterModel]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const applyFilter = useCallback(
    (text: string, column: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceVersionRef.current += 1;
      const runVersion = debounceVersionRef.current;

      debounceRef.current = setTimeout(() => {
        if (runVersion !== debounceVersionRef.current) {
          return;
        }

        const currentItems =
          (apiRef.current.state as any).filter?.filterModel?.items ?? [];
        const baseItems = currentItems.filter(
          (item: any) => item.id !== '__column_search__',
        );
        if (column === 'all') {
          apiRef.current.setFilterModel({
            items: baseItems,
            quickFilterValues: text ? [text] : [],
          });
        } else {
          const operatorValue = isNumeric(columns, column) ? '=' : 'contains';
          apiRef.current.setFilterModel({
            items: text
              ? [
                  ...baseItems,
                  {
                    id: '__column_search__',
                    columnField: column,
                    operatorValue,
                    value: text,
                  },
                ]
              : baseItems,
            quickFilterValues: [],
          });
        }

        debounceRef.current = null;
      }, 300);
    },
    [apiRef, columns],
  );

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (
      isNumeric(columns, selectedColumn) &&
      val !== '' &&
      !/^\d+$/.test(val)
    ) {
      return;
    }
    setSearchText(val);
    applyFilter(val, selectedColumn);
  };

  const handleColumnChange = (e: any) => {
    const col = e.target.value as string;
    setSelectedColumn(col);
    setSearchText('');
    const currentItems =
      (apiRef.current.state as any).filter?.filterModel?.items ?? [];
    const baseItems = currentItems.filter(
      (item: any) => item.id !== '__column_search__',
    );
    apiRef.current.setFilterModel({ items: baseItems, quickFilterValues: [] });
  };

  const handleClear = () => {
    setSearchText('');
    setSelectedColumn('all');
    const currentItems =
      (apiRef.current.state as any).filter?.filterModel?.items ?? [];
    const baseItems = currentItems.filter(
      (item: any) => item.id !== '__column_search__',
    );
    apiRef.current.setFilterModel({ items: baseItems, quickFilterValues: [] });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: 1,
        minWidth: { xs: 220, sm: 280, md: 380 },
        height: { xs: 30, sm: 34 },
        border: '1px solid #d0e4c8',
        borderRadius: '6px',
        backgroundColor: '#f4f7f2',
        overflow: 'hidden',
      }}
    >
      <InputAdornment position="start" sx={{ pl: '6px', color: '#69bc46' }}>
        <SearchIcon fontSize="small" />
      </InputAdornment>
      <TextField
        variant="standard"
        size="small"
        value={searchText}
        onChange={handleTextChange}
        placeholder={t('attributes.search', { defaultValue: 'Search...' })}
        InputProps={{
          disableUnderline: true,
          endAdornment: searchText ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} sx={{ p: '2px' }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
        sx={{
          flex: 1,
          '& .MuiInputBase-input': {
            fontSize: { xs: '0.9rem', sm: '0.95rem' },
            padding: '0 4px',
            height: { xs: 30, sm: 34 },
            boxSizing: 'border-box',
          },
          '& input': { boxShadow: 'none !important' },
        }}
      />
      <Select
        value={selectedColumn}
        onChange={handleColumnChange}
        variant="standard"
        disableUnderline
        size="small"
        sx={{
          fontSize: { xs: '0.82rem', sm: '0.88rem' },
          backgroundColor: '#69bc46',
          color: '#fff',
          minWidth: 80,
          height: { xs: 30, sm: 34 },
          pl: '8px',
          borderLeft: '1px solid #5aab38',
          '& .MuiSelect-icon': { color: '#fff' },
          '& .MuiSelect-select': {
            pb: '0 !important',
            pt: '0 !important',
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {columns.map((col) => (
          <MenuItem
            key={col.value}
            value={col.value}
            sx={{ fontSize: '0.9rem' }}
          >
            {col.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
